import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCreditCardOutline, mdiOpenInNew, mdiPencilOutline } from "@mdi/js";
import CardShell from "./CardShell";
import SendButton from "./SendButton";
import {
  PaymentDetail,
  amountModifier,
  paymentAmount,
  stripeButtonFor,
} from "./PaymentDetail";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import { formatMoney } from "app/containers/Jobs/utils/format";
import { computePaymentActions } from "app/containers/Jobs/utils/paymentActionRules";

// Once a request is out, Mark as Paid would 409, so unpaid offers Cancel
// Request instead.
const PaymentCard = ({
  job,
  tier = "Solo",
  onEditQuote,
  onUpgrade,
  onSendPaymentRequest,
  onMarkPaid,
  onCancelRequest,
  onCopyRequestLink,
  onUndoPayment,
  onRestartPayment,
  onOpenStripeDashboard,
  sendTrack = {},
  busy = false,
  timeZone,
}) => {
  const payment = job.payment || {};
  const status = payment.status;
  const amount = paymentAmount(job);

  const shell = (body, { headerRight, footerLeft, footerRight }) => (
    <CardShell
      icon={
        <Icon path={mdiCreditCardOutline} size={0.7} className="jd-card-icon" />
      }
      title="Payment"
      headerRight={headerRight}
      className="jd-card--half"
      footerLeft={footerLeft}
      footerRight={footerRight}
    >
      {body}
    </CardShell>
  );

  // Free tier never reaches a payment state it could act on, so the card
  // becomes the upsell. A downgraded user with a request out keeps the normal
  // card, since Cancel Request must stay reachable.
  if (tier === "Free" && status === "no_payment") {
    return shell(
      <div className="jd-muted-heading">
        Send request &amp; get paid online
      </div>,
      {
        headerRight: <span className="jd-chip jd-chip--info">Solo</span>,
        footerRight: (
          <Button variant="primary" size="sm" onClick={onUpgrade}>
            Upgrade to Solo
          </Button>
        ),
      }
    );
  }

  const badge = <JobStatusBadge domain="payment" value={status} />;

  if (status === "no_payment" && amount == null) {
    return shell(
      <>
        <div className="jd-value-strong jd-payment-amount jd-payment-amount--empty">
          -
        </div>
        <div className="jd-subtle">Set a quote to request payment.</div>
      </>,
      {
        headerRight: badge,
        footerRight: (
          <Button variant="primary" size="sm" onClick={onEditQuote}>
            Set Quote
          </Button>
        ),
      }
    );
  }

  const { canMarkPaid, canCancelRequest, canUndo, canRestart, canEditQuote } =
    computePaymentActions({ status, paymentId: payment.id, tier });
  const stripeButton = stripeButtonFor(status);

  const actions = (
    <>
      {canMarkPaid && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={onMarkPaid}
        >
          Mark as Paid
        </Button>
      )}
      {canCancelRequest && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={() => onCancelRequest?.(payment.id)}
        >
          Cancel Request
        </Button>
      )}
      {status === "unpaid" && payment.request_url && (
        <Button
          variant="outline-primary"
          size="sm"
          disabled={busy}
          onClick={() => onCopyRequestLink?.(payment.request_url)}
        >
          Copy Request Link
        </Button>
      )}
      {canUndo && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={() => onUndoPayment?.(payment.id)}
        >
          Undo
        </Button>
      )}
      {canRestart && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={() => onRestartPayment?.(payment.id)}
        >
          Restart Payment
        </Button>
      )}
      {stripeButton && (
        <Button
          variant={stripeButton.variant}
          size="sm"
          className="jd-btn-with-icon"
          disabled={busy}
          onClick={() => onOpenStripeDashboard()}
        >
          {stripeButton.label}
          <Icon path={mdiOpenInNew} size={0.6} />
        </Button>
      )}
      {status === "no_payment" && (
        <SendButton
          track={sendTrack}
          sending={busy}
          onClick={onSendPaymentRequest}
        >
          Send Payment Request
        </SendButton>
      )}
    </>
  );

  return shell(
    <>
      <div
        className={`jd-value-strong jd-payment-amount ${amountModifier(
          status
        )}`.trim()}
      >
        {amount != null ? formatMoney(amount) : "-"}
      </div>
      <PaymentDetail payment={payment} timeZone={timeZone} />
    </>,
    {
      headerRight: badge,
      footerLeft: canEditQuote && (
        <span className="jd-card-footer-note">
          Edit quote
          <Icon
            path={mdiPencilOutline}
            size={0.5}
            className="jd-footer-edit"
            onClick={onEditQuote}
          />
        </span>
      ),
      footerRight: actions,
    }
  );
};

export default PaymentCard;
